/**
 * MerchantActivityInsights - Admin Panel
 * Platform-wide merchant activity analytics, engagement scoring, and churn risk monitoring
 * CSS prefix: mai- (merchant activity insights)
 */

import React, { useState, useEffect, useCallback } from 'react';
import usePolling from '../hooks/usePolling';
import './MerchantActivityInsights.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

/* =============================================
   SVG ICON COMPONENTS (Feather/Lucide style)
   ============================================= */

const Icons = {
  Activity: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  Users: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  UserCheck: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" />
    </svg>
  ),
  AlertTriangle: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  TrendingUp: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  TrendingDown: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" />
    </svg>
  ),
  BarChart: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
    </svg>
  ),
  Clock: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  Eye: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Zap: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  Globe: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Smartphone: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" /><line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  ),
  Monitor: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  Calendar: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  RefreshCw: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  Search: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  X: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  ChevronRight: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  ExternalLink: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  ),
  Mail: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  Target: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
  ),
  LogIn: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
    </svg>
  ),
  ShoppingCart: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  ),
  Package: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  MessageSquare: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  CreditCard: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  Settings: (props) => (
    <svg width={props.size || 24} height={props.size || 24} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
      <circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

/* =============================================
   UTILITY FUNCTIONS
   ============================================= */

const formatNumber = (num) => (Number(num) || 0).toLocaleString();

const formatPercent = (num) => `${(Number(num) || 0).toFixed(1)}%`;

const formatDuration = (minutes) => {
  const m = Number(minutes) || 0;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const getEngagementColor = (score) => {
  const s = Number(score) || 0;
  if (s >= 70) return 'mai-score-high';
  if (s >= 40) return 'mai-score-medium';
  return 'mai-score-low';
};

const getChurnColor = (score) => {
  const s = Number(score) || 0;
  if (s >= 70) return 'mai-churn-high';
  if (s >= 40) return 'mai-churn-medium';
  return 'mai-churn-low';
};

const getDeviceIcon = (deviceType) => {
  if (!deviceType) return <Icons.Globe size={16} />;
  const d = deviceType.toLowerCase();
  if (d.includes('mobile') || d.includes('ios') || d.includes('android')) return <Icons.Smartphone size={16} />;
  return <Icons.Monitor size={16} />;
};

/* =============================================
   MAIN COMPONENT
   ============================================= */

const MerchantActivityInsights = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Toast notification
  const [toast, setToast] = useState(null);

  // Loading / Error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Overview data
  const [overview, setOverview] = useState(null);

  // Active sessions
  const [activeSessions, setActiveSessions] = useState([]);

  // At-risk merchants
  const [atRiskMerchants, setAtRiskMerchants] = useState([]);
  const [atRiskPage, setAtRiskPage] = useState(1);
  const [atRiskTotal, setAtRiskTotal] = useState(0);

  // Top engaged merchants
  const [topEngaged, setTopEngaged] = useState([]);
  const [engagedPage, setEngagedPage] = useState(1);
  const [engagedTotal, setEngagedTotal] = useState(0);

  // Feature adoption
  const [featureAdoption, setFeatureAdoption] = useState([]);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Merchant detail modal
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchantDetail, setMerchantDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Helpers
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('adminToken');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  // Fetch overview data
  const fetchOverview = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/activity/overview`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch overview');
      const data = await res.json();
      setOverview(data);
    } catch (err) {
      console.error('Overview fetch error:', err);
    }
  }, [getAuthHeaders]);

  // Fetch active sessions
  const fetchActiveSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/activity/sessions?active=true`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch sessions');
      const data = await res.json();
      setActiveSessions(data.sessions || []);
    } catch (err) {
      console.error('Sessions fetch error:', err);
    }
  }, [getAuthHeaders]);

  // Fetch at-risk merchants
  const fetchAtRiskMerchants = useCallback(async (page = 1) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/activity/churn-risk?page=${page}&limit=10`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch at-risk merchants');
      const data = await res.json();
      setAtRiskMerchants(data.merchants || []);
      setAtRiskTotal(data.total || 0);
    } catch (err) {
      console.error('At-risk fetch error:', err);
    }
  }, [getAuthHeaders]);

  // Fetch top engaged merchants
  const fetchTopEngaged = useCallback(async (page = 1) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/activity/engagement?page=${page}&limit=10`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch engaged merchants');
      const data = await res.json();
      setTopEngaged(data.merchants || []);
      setEngagedTotal(data.total || 0);
    } catch (err) {
      console.error('Engaged fetch error:', err);
    }
  }, [getAuthHeaders]);

  // Fetch feature adoption
  const fetchFeatureAdoption = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/activity/feature-adoption`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch feature adoption');
      const data = await res.json();
      setFeatureAdoption(data.features || []);
    } catch (err) {
      console.error('Feature adoption fetch error:', err);
    }
  }, [getAuthHeaders]);

  // Fetch merchant detail
  const fetchMerchantDetail = useCallback(async (merchantId) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/activity/merchants/${merchantId}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to fetch merchant detail');
      const data = await res.json();
      setMerchantDetail(data);
    } catch (err) {
      console.error('Merchant detail fetch error:', err);
      showToast('Failed to load merchant details', 'error');
    } finally {
      setLoadingDetail(false);
    }
  }, [getAuthHeaders, showToast]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchOverview(),
          fetchActiveSessions(),
          fetchAtRiskMerchants(1),
          fetchTopEngaged(1),
          fetchFeatureAdoption()
        ]);
      } catch (err) {
        setError('Failed to load activity data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchOverview, fetchActiveSessions, fetchAtRiskMerchants, fetchTopEngaged, fetchFeatureAdoption]);

  // Auto-refresh sessions every 60s while on the sessions tab AND the
  // browser tab is visible. Was 30s and unconditional — pure edge bleed
  // when the admin had the tab open in the background.
  usePolling(
    fetchActiveSessions,
    60_000,
    [fetchActiveSessions],
    { enabled: activeTab === 'sessions' },
  );

  // Open merchant detail
  const openMerchantDetail = (merchant) => {
    setSelectedMerchant(merchant);
    fetchMerchantDetail(merchant.id);
  };

  // Close detail modal
  const closeDetail = () => {
    setSelectedMerchant(null);
    setMerchantDetail(null);
  };

  // Refresh all
  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchOverview(),
      fetchActiveSessions(),
      fetchAtRiskMerchants(atRiskPage),
      fetchTopEngaged(engagedPage),
      fetchFeatureAdoption()
    ]);
    setLoading(false);
    showToast('Data refreshed');
  };

  // Render loading state
  if (loading && !overview) {
    return (
      <div className="mai-container">
        <div className="mai-loading">
          <div className="mai-spinner" />
          <p>Loading activity insights...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !overview) {
    return (
      <div className="mai-container">
        <div className="mai-error">
          <Icons.AlertTriangle size={48} />
          <h2>Failed to Load</h2>
          <p>{error}</p>
          <button onClick={refreshAll} className="mai-btn mai-btn-primary">
            <Icons.RefreshCw size={16} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mai-container">
      {/* Toast Notification */}
      {toast && (
        <div className={`mai-toast mai-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mai-header">
        <div className="mai-header-left">
          <div className="mai-header-icon">
            <Icons.Activity size={28} />
          </div>
          <div>
            <h1 className="mai-title">Merchant Activity Insights</h1>
            <p className="mai-subtitle">Platform-wide engagement analytics and churn monitoring</p>
          </div>
        </div>
        <button
          className="mai-btn mai-btn-secondary"
          onClick={refreshAll}
          disabled={loading}
        >
          <Icons.RefreshCw size={16} className={loading ? 'mai-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Overview Stats */}
      <div className="mai-stats-grid">
        <div className="mai-stat-card mai-stat-green">
          <div className="mai-stat-icon">
            <Icons.Users size={24} />
          </div>
          <div className="mai-stat-content">
            <span className="mai-stat-value">{formatNumber(overview?.activeSessions || 0)}</span>
            <span className="mai-stat-label">Active Now</span>
          </div>
        </div>

        <div className="mai-stat-card mai-stat-blue">
          <div className="mai-stat-icon">
            <Icons.UserCheck size={24} />
          </div>
          <div className="mai-stat-content">
            <span className="mai-stat-value">{formatNumber(overview?.dailyActiveUsers || 0)}</span>
            <span className="mai-stat-label">Daily Active</span>
          </div>
        </div>

        <div className="mai-stat-card mai-stat-purple">
          <div className="mai-stat-icon">
            <Icons.Calendar size={24} />
          </div>
          <div className="mai-stat-content">
            <span className="mai-stat-value">{formatNumber(overview?.weeklyActiveUsers || 0)}</span>
            <span className="mai-stat-label">Weekly Active</span>
          </div>
        </div>

        <div className="mai-stat-card mai-stat-teal">
          <div className="mai-stat-icon">
            <Icons.TrendingUp size={24} />
          </div>
          <div className="mai-stat-content">
            <span className="mai-stat-value">{formatPercent(overview?.avgEngagementScore || 0)}</span>
            <span className="mai-stat-label">Avg Engagement</span>
          </div>
        </div>

        <div className="mai-stat-card mai-stat-orange">
          <div className="mai-stat-icon">
            <Icons.AlertTriangle size={24} />
          </div>
          <div className="mai-stat-content">
            <span className="mai-stat-value">{formatNumber(overview?.atRiskCount || 0)}</span>
            <span className="mai-stat-label">At Risk</span>
          </div>
        </div>

        <div className="mai-stat-card mai-stat-gray">
          <div className="mai-stat-icon">
            <Icons.Clock size={24} />
          </div>
          <div className="mai-stat-content">
            <span className="mai-stat-value">{formatDuration(overview?.avgSessionMinutes || 0)}</span>
            <span className="mai-stat-label">Avg Session</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mai-tabs">
        <button
          className={`mai-tab ${activeTab === 'overview' ? 'mai-tab-active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <Icons.BarChart size={16} /> Overview
        </button>
        <button
          className={`mai-tab ${activeTab === 'sessions' ? 'mai-tab-active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          <Icons.Zap size={16} /> Active Sessions
          {activeSessions.length > 0 && (
            <span className="mai-tab-badge">{activeSessions.length}</span>
          )}
        </button>
        <button
          className={`mai-tab ${activeTab === 'at-risk' ? 'mai-tab-active' : ''}`}
          onClick={() => setActiveTab('at-risk')}
        >
          <Icons.AlertTriangle size={16} /> At Risk
          {(overview?.atRiskCount || 0) > 0 && (
            <span className="mai-tab-badge mai-tab-badge-warning">{overview.atRiskCount}</span>
          )}
        </button>
        <button
          className={`mai-tab ${activeTab === 'engaged' ? 'mai-tab-active' : ''}`}
          onClick={() => setActiveTab('engaged')}
        >
          <Icons.TrendingUp size={16} /> Top Engaged
        </button>
        <button
          className={`mai-tab ${activeTab === 'features' ? 'mai-tab-active' : ''}`}
          onClick={() => setActiveTab('features')}
        >
          <Icons.Target size={16} /> Feature Adoption
        </button>
      </div>

      {/* Tab Content */}
      <div className="mai-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="mai-overview">
            <div className="mai-overview-grid">
              {/* Login Trends */}
              <div className="mai-panel">
                <h3 className="mai-panel-title">
                  <Icons.LogIn size={18} /> Login Activity
                </h3>
                <div className="mai-mini-stats">
                  <div className="mai-mini-stat">
                    <span className="mai-mini-value">{formatNumber(overview?.todayLogins || 0)}</span>
                    <span className="mai-mini-label">Today</span>
                  </div>
                  <div className="mai-mini-stat">
                    <span className="mai-mini-value">{formatNumber(overview?.weekLogins || 0)}</span>
                    <span className="mai-mini-label">This Week</span>
                  </div>
                  <div className="mai-mini-stat">
                    <span className="mai-mini-value">{formatNumber(overview?.monthLogins || 0)}</span>
                    <span className="mai-mini-label">This Month</span>
                  </div>
                </div>
              </div>

              {/* Platform Usage */}
              <div className="mai-panel">
                <h3 className="mai-panel-title">
                  <Icons.Globe size={18} /> Platform Usage
                </h3>
                <div className="mai-platform-stats">
                  <div className="mai-platform-item">
                    <Icons.Monitor size={20} />
                    <span className="mai-platform-label">Web</span>
                    <span className="mai-platform-value">{formatPercent(overview?.webUsage || 0)}</span>
                  </div>
                  <div className="mai-platform-item">
                    <Icons.Smartphone size={20} />
                    <span className="mai-platform-label">Mobile</span>
                    <span className="mai-platform-value">{formatPercent(overview?.mobileUsage || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Score Distribution */}
              <div className="mai-panel">
                <h3 className="mai-panel-title">
                  <Icons.BarChart size={18} /> Engagement Distribution
                </h3>
                <div className="mai-score-bars">
                  <div className="mai-score-bar-item">
                    <span className="mai-score-bar-label">High (70+)</span>
                    <div className="mai-score-bar-track">
                      <div
                        className="mai-score-bar-fill mai-score-bar-high"
                        style={{ width: `${overview?.engagementHigh || 0}%` }}
                      />
                    </div>
                    <span className="mai-score-bar-value">{formatPercent(overview?.engagementHigh || 0)}</span>
                  </div>
                  <div className="mai-score-bar-item">
                    <span className="mai-score-bar-label">Medium (40-69)</span>
                    <div className="mai-score-bar-track">
                      <div
                        className="mai-score-bar-fill mai-score-bar-medium"
                        style={{ width: `${overview?.engagementMedium || 0}%` }}
                      />
                    </div>
                    <span className="mai-score-bar-value">{formatPercent(overview?.engagementMedium || 0)}</span>
                  </div>
                  <div className="mai-score-bar-item">
                    <span className="mai-score-bar-label">Low (&lt;40)</span>
                    <div className="mai-score-bar-track">
                      <div
                        className="mai-score-bar-fill mai-score-bar-low"
                        style={{ width: `${overview?.engagementLow || 0}%` }}
                      />
                    </div>
                    <span className="mai-score-bar-value">{formatPercent(overview?.engagementLow || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="mai-panel">
                <h3 className="mai-panel-title">
                  <Icons.Zap size={18} /> Quick Stats
                </h3>
                <div className="mai-quick-stats">
                  <div className="mai-quick-item">
                    <span className="mai-quick-label">Total Page Views Today</span>
                    <span className="mai-quick-value">{formatNumber(overview?.todayPageViews || 0)}</span>
                  </div>
                  <div className="mai-quick-item">
                    <span className="mai-quick-label">Total Actions Today</span>
                    <span className="mai-quick-value">{formatNumber(overview?.todayActions || 0)}</span>
                  </div>
                  <div className="mai-quick-item">
                    <span className="mai-quick-label">Avg Pages Per Session</span>
                    <span className="mai-quick-value">{(overview?.avgPagesPerSession || 0).toFixed(1)}</span>
                  </div>
                  <div className="mai-quick-item">
                    <span className="mai-quick-label">Never Logged In</span>
                    <span className="mai-quick-value">{formatNumber(overview?.neverLoggedIn || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="mai-sessions">
            <div className="mai-section-header">
              <h3>Currently Active Merchants</h3>
              <span className="mai-badge mai-badge-green">
                {activeSessions.length} online
              </span>
            </div>

            {activeSessions.length === 0 ? (
              <div className="mai-empty">
                <Icons.Users size={48} />
                <p>No active sessions right now</p>
              </div>
            ) : (
              <div className="mai-table-container">
                <table className="mai-table">
                  <thead>
                    <tr>
                      <th>Merchant</th>
                      <th>Platform</th>
                      <th>Location</th>
                      <th>Duration</th>
                      <th>Pages</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeSessions.map((session) => (
                      <tr key={session.id} onClick={() => openMerchantDetail(session.merchant)}>
                        <td>
                          <div className="mai-merchant-cell">
                            <span className="mai-online-dot" />
                            <div>
                              <div className="mai-merchant-name">
                                {session.merchant?.store?.name || session.merchant?.email}
                              </div>
                              <div className="mai-merchant-email">{session.merchant?.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="mai-platform-badge">
                            {getDeviceIcon(session.deviceType)}
                            <span>{session.platform || 'web'}</span>
                          </div>
                        </td>
                        <td>
                          <span className="mai-location">
                            {session.city && session.country
                              ? `${session.city}, ${session.country}`
                              : session.country || '-'}
                          </span>
                        </td>
                        <td>{formatDuration(session.durationMinutes || 0)}</td>
                        <td>{formatNumber(session.pagesViewed || 0)}</td>
                        <td>
                          <button
                            className="mai-btn-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              openMerchantDetail(session.merchant);
                            }}
                          >
                            <Icons.Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* At Risk Tab */}
        {activeTab === 'at-risk' && (
          <div className="mai-at-risk">
            <div className="mai-section-header">
              <h3>At-Risk Merchants</h3>
              <span className="mai-badge mai-badge-orange">
                {atRiskTotal} merchants with high churn risk
              </span>
            </div>

            {atRiskMerchants.length === 0 ? (
              <div className="mai-empty mai-empty-success">
                <Icons.UserCheck size={48} />
                <p>No at-risk merchants detected</p>
                <span>All merchants are engaged and active</span>
              </div>
            ) : (
              <>
                <div className="mai-table-container">
                  <table className="mai-table">
                    <thead>
                      <tr>
                        <th>Merchant</th>
                        <th>Churn Risk</th>
                        <th>Engagement</th>
                        <th>Last Active</th>
                        <th>Days Inactive</th>
                        <th>Risk Factors</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {atRiskMerchants.map((merchant) => (
                        <tr key={merchant.id}>
                          <td>
                            <div className="mai-merchant-cell">
                              <div>
                                <div className="mai-merchant-name">
                                  {merchant.store?.name || merchant.email}
                                </div>
                                <div className="mai-merchant-email">{merchant.email}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`mai-score-badge ${getChurnColor(merchant.churnRiskScore)}`}>
                              {formatPercent(merchant.churnRiskScore)}
                            </span>
                          </td>
                          <td>
                            <span className={`mai-score-badge ${getEngagementColor(merchant.engagementScore)}`}>
                              {formatPercent(merchant.engagementScore)}
                            </span>
                          </td>
                          <td>{formatTimeAgo(merchant.lastActivity)}</td>
                          <td>
                            <span className={merchant.daysSinceLastLogin > 14 ? 'mai-text-danger' : ''}>
                              {merchant.daysSinceLastLogin || 0} days
                            </span>
                          </td>
                          <td>
                            <div className="mai-risk-factors">
                              {merchant.daysSinceLastLogin > 7 && (
                                <span className="mai-risk-tag">Inactive</span>
                              )}
                              {merchant.engagementScore < 30 && (
                                <span className="mai-risk-tag">Low Engagement</span>
                              )}
                              {merchant.totalSessionMinutes < 30 && (
                                <span className="mai-risk-tag">Low Usage</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="mai-actions">
                              <button
                                className="mai-btn-icon"
                                title="View Details"
                                onClick={() => openMerchantDetail(merchant)}
                              >
                                <Icons.Eye size={16} />
                              </button>
                              <button
                                className="mai-btn-icon"
                                title="Send Re-engagement Email"
                              >
                                <Icons.Mail size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {atRiskTotal > 10 && (
                  <div className="mai-pagination">
                    <button
                      className="mai-btn mai-btn-sm"
                      disabled={atRiskPage === 1}
                      onClick={() => {
                        setAtRiskPage(p => p - 1);
                        fetchAtRiskMerchants(atRiskPage - 1);
                      }}
                    >
                      Previous
                    </button>
                    <span className="mai-page-info">
                      Page {atRiskPage} of {Math.ceil(atRiskTotal / 10)}
                    </span>
                    <button
                      className="mai-btn mai-btn-sm"
                      disabled={atRiskPage >= Math.ceil(atRiskTotal / 10)}
                      onClick={() => {
                        setAtRiskPage(p => p + 1);
                        fetchAtRiskMerchants(atRiskPage + 1);
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Top Engaged Tab */}
        {activeTab === 'engaged' && (
          <div className="mai-engaged">
            <div className="mai-section-header">
              <h3>Most Engaged Merchants</h3>
            </div>

            <div className="mai-table-container">
              <table className="mai-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Merchant</th>
                    <th>Engagement Score</th>
                    <th>Sessions</th>
                    <th>Time Spent</th>
                    <th>Page Views</th>
                    <th>Features Used</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {topEngaged.map((merchant, index) => (
                    <tr key={merchant.id}>
                      <td>
                        <span className="mai-rank">
                          {(engagedPage - 1) * 10 + index + 1}
                        </span>
                      </td>
                      <td>
                        <div className="mai-merchant-cell">
                          <div>
                            <div className="mai-merchant-name">
                              {merchant.store?.name || merchant.email}
                            </div>
                            <div className="mai-merchant-email">{merchant.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`mai-score-badge ${getEngagementColor(merchant.engagementScore)}`}>
                          {formatPercent(merchant.engagementScore)}
                        </span>
                      </td>
                      <td>{formatNumber(merchant.loginCount || 0)}</td>
                      <td>{formatDuration(merchant.totalSessionMinutes || 0)}</td>
                      <td>{formatNumber(merchant.totalPageViews || 0)}</td>
                      <td>{formatNumber(merchant.featuresUsedCount || 0)}</td>
                      <td>
                        <button
                          className="mai-btn-icon"
                          title="View Details"
                          onClick={() => openMerchantDetail(merchant)}
                        >
                          <Icons.Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {engagedTotal > 10 && (
              <div className="mai-pagination">
                <button
                  className="mai-btn mai-btn-sm"
                  disabled={engagedPage === 1}
                  onClick={() => {
                    setEngagedPage(p => p - 1);
                    fetchTopEngaged(engagedPage - 1);
                  }}
                >
                  Previous
                </button>
                <span className="mai-page-info">
                  Page {engagedPage} of {Math.ceil(engagedTotal / 10)}
                </span>
                <button
                  className="mai-btn mai-btn-sm"
                  disabled={engagedPage >= Math.ceil(engagedTotal / 10)}
                  onClick={() => {
                    setEngagedPage(p => p + 1);
                    fetchTopEngaged(engagedPage + 1);
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Feature Adoption Tab */}
        {activeTab === 'features' && (
          <div className="mai-features">
            <div className="mai-section-header">
              <h3>Feature Adoption Rates</h3>
              <p className="mai-section-desc">Percentage of merchants actively using each feature</p>
            </div>

            <div className="mai-feature-grid">
              {featureAdoption.map((feature) => (
                <div key={feature.featureId} className="mai-feature-card">
                  <div className="mai-feature-header">
                    <div className="mai-feature-icon">
                      {feature.featureId === 'orders' && <Icons.ShoppingCart size={20} />}
                      {feature.featureId === 'products' && <Icons.Package size={20} />}
                      {feature.featureId === 'pos' && <Icons.CreditCard size={20} />}
                      {feature.featureId === 'customers' && <Icons.Users size={20} />}
                      {feature.featureId === 'sms' && <Icons.MessageSquare size={20} />}
                      {feature.featureId === 'analytics' && <Icons.BarChart size={20} />}
                      {feature.featureId === 'settings' && <Icons.Settings size={20} />}
                      {!['orders', 'products', 'pos', 'customers', 'sms', 'analytics', 'settings'].includes(feature.featureId) && <Icons.Zap size={20} />}
                    </div>
                    <div className="mai-feature-info">
                      <span className="mai-feature-name">{feature.name || feature.featureId}</span>
                      <span className="mai-feature-users">{formatNumber(feature.activeUsers)} users</span>
                    </div>
                  </div>
                  <div className="mai-feature-bar">
                    <div
                      className="mai-feature-fill"
                      style={{ width: `${feature.adoptionRate || 0}%` }}
                    />
                  </div>
                  <div className="mai-feature-footer">
                    <span className="mai-feature-rate">{formatPercent(feature.adoptionRate)}</span>
                    <span className="mai-feature-trend">
                      {feature.trend > 0 ? <Icons.TrendingUp size={14} /> : feature.trend < 0 ? <Icons.TrendingDown size={14} /> : null}
                      {feature.trend !== 0 && `${feature.trend > 0 ? '+' : ''}${feature.trend}%`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Merchant Detail Modal */}
      {selectedMerchant && (
        <div className="mai-modal-overlay" onClick={closeDetail}>
          <div className="mai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mai-modal-header">
              <h2>Merchant Activity</h2>
              <button className="mai-modal-close" onClick={closeDetail}>
                <Icons.X size={20} />
              </button>
            </div>

            {loadingDetail ? (
              <div className="mai-modal-loading">
                <div className="mai-spinner" />
                <p>Loading merchant data...</p>
              </div>
            ) : merchantDetail ? (
              <div className="mai-modal-body">
                {/* Merchant Info */}
                <div className="mai-detail-header">
                  <div className="mai-detail-info">
                    <h3>{merchantDetail.store?.name || merchantDetail.email}</h3>
                    <p>{merchantDetail.email}</p>
                    <span className="mai-tier-badge">{merchantDetail.merchantTier || 'FREE'}</span>
                  </div>
                  <a
                    href={`/merchants/${merchantDetail.id}`}
                    className="mai-btn mai-btn-sm"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icons.ExternalLink size={14} /> View Profile
                  </a>
                </div>

                {/* Score Cards */}
                <div className="mai-detail-scores">
                  <div className="mai-detail-score">
                    <span className="mai-detail-score-label">Engagement Score</span>
                    <span className={`mai-detail-score-value ${getEngagementColor(merchantDetail.engagementScore)}`}>
                      {formatPercent(merchantDetail.engagementScore)}
                    </span>
                  </div>
                  <div className="mai-detail-score">
                    <span className="mai-detail-score-label">Churn Risk</span>
                    <span className={`mai-detail-score-value ${getChurnColor(merchantDetail.churnRiskScore)}`}>
                      {formatPercent(merchantDetail.churnRiskScore)}
                    </span>
                  </div>
                </div>

                {/* Activity Stats */}
                <div className="mai-detail-stats">
                  <div className="mai-detail-stat">
                    <Icons.LogIn size={18} />
                    <div>
                      <span className="mai-detail-stat-value">{formatNumber(merchantDetail.loginCount)}</span>
                      <span className="mai-detail-stat-label">Total Logins</span>
                    </div>
                  </div>
                  <div className="mai-detail-stat">
                    <Icons.Clock size={18} />
                    <div>
                      <span className="mai-detail-stat-value">{formatDuration(merchantDetail.totalSessionMinutes)}</span>
                      <span className="mai-detail-stat-label">Total Time</span>
                    </div>
                  </div>
                  <div className="mai-detail-stat">
                    <Icons.Eye size={18} />
                    <div>
                      <span className="mai-detail-stat-value">{formatNumber(merchantDetail.totalPageViews)}</span>
                      <span className="mai-detail-stat-label">Page Views</span>
                    </div>
                  </div>
                  <div className="mai-detail-stat">
                    <Icons.Zap size={18} />
                    <div>
                      <span className="mai-detail-stat-value">{formatNumber(merchantDetail.featuresUsedCount)}</span>
                      <span className="mai-detail-stat-label">Features Used</span>
                    </div>
                  </div>
                </div>

                {/* Last Activity */}
                <div className="mai-detail-section">
                  <h4>Activity Timeline</h4>
                  <div className="mai-timeline">
                    <div className="mai-timeline-item">
                      <span className="mai-timeline-label">Last Login</span>
                      <span className="mai-timeline-value">{formatTimeAgo(merchantDetail.lastLogin)}</span>
                    </div>
                    <div className="mai-timeline-item">
                      <span className="mai-timeline-label">Last Activity</span>
                      <span className="mai-timeline-value">{formatTimeAgo(merchantDetail.lastActivity)}</span>
                    </div>
                    <div className="mai-timeline-item">
                      <span className="mai-timeline-label">Days Since Login</span>
                      <span className="mai-timeline-value">{merchantDetail.daysSinceLastLogin || 0} days</span>
                    </div>
                    <div className="mai-timeline-item">
                      <span className="mai-timeline-label">Avg Session Duration</span>
                      <span className="mai-timeline-value">{formatDuration(merchantDetail.avgSessionMinutes)}</span>
                    </div>
                  </div>
                </div>

                {/* Recent Sessions */}
                {merchantDetail.recentSessions && merchantDetail.recentSessions.length > 0 && (
                  <div className="mai-detail-section">
                    <h4>Recent Sessions</h4>
                    <div className="mai-sessions-list">
                      {merchantDetail.recentSessions.slice(0, 5).map((session) => (
                        <div key={session.id} className="mai-session-item">
                          <div className="mai-session-platform">
                            {getDeviceIcon(session.deviceType)}
                            <span>{session.platform || 'web'}</span>
                          </div>
                          <div className="mai-session-meta">
                            <span>{formatDate(session.startedAt)}</span>
                            <span>{formatDuration(session.durationMinutes || 0)}</span>
                            <span>{session.pagesViewed || 0} pages</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Screen Names / Pages Visited */}
                {merchantDetail.pageViewsByScreen && merchantDetail.pageViewsByScreen.length > 0 && (
                  <div className="mai-detail-section">
                    <h4>Screens Visited</h4>
                    <div className="mai-screen-list">
                      {merchantDetail.pageViewsByScreen.map((screen, idx) => (
                        <div key={idx} className="mai-screen-item">
                          <span className="mai-screen-name">{screen.page || 'Unknown'}</span>
                          <span className="mai-screen-count">{formatNumber(screen.count)} views</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Feature Usage */}
                {merchantDetail.featureUsage && merchantDetail.featureUsage.length > 0 && (
                  <div className="mai-detail-section">
                    <h4>Features Used</h4>
                    <div className="mai-feature-list">
                      {merchantDetail.featureUsage.map((feature) => (
                        <div key={feature.featureId} className="mai-feature-item">
                          <span className="mai-feature-name">{feature.featureId}</span>
                          <span className="mai-feature-count">{formatNumber(feature.usageCount)} uses</span>
                          <span className="mai-feature-last">{formatTimeAgo(feature.lastUsedAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Page Views */}
                {merchantDetail.recentPageViews && merchantDetail.recentPageViews.length > 0 && (
                  <div className="mai-detail-section">
                    <h4>Recent Activity</h4>
                    <div className="mai-activity-list">
                      {merchantDetail.recentPageViews.slice(0, 10).map((activity) => (
                        <div key={activity.id} className="mai-activity-item">
                          <span className="mai-activity-page">{activity.page || 'Page View'}</span>
                          <span className="mai-activity-platform">{activity.platform || 'web'}</span>
                          <span className="mai-activity-time">{formatTimeAgo(activity.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mai-modal-error">
                <p>Failed to load merchant details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantActivityInsights;
